'use client';

import { ColumnDef } from '@tanstack/react-table';
import { Loader2, RotateCw, Trash2 } from 'lucide-react';

import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { DataTable } from '@/components/ui/DataTable';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Member } from '@/lib/query/useOrganizationMembers';

interface MembersTableProps {
  members: Member[];
  isLoading?: boolean;
  isAdmin: boolean;
  currentUserEmail?: string;
  onRoleChange?: (memberId: string, newRole: 'admin' | 'member') => void;
  onRemove?: (member: Member) => void;
  /** Map of invitee email → Clerk invitation external id, for pending-row actions. */
  invitationClerkIdByEmail?: Record<string, string>;
  onResendInvite?: (clerkInvitationId: string) => void;
  onRevokeInvite?: (member: Member, clerkInvitationId: string) => void;
  /** External id of the invitation currently being resent (drives the per-row spinner). */
  resendingInvitationId?: string | null;
  /** True when more members remain to load (drives the "Load more" button). */
  hasMore?: boolean;
  onLoadMore?: () => void;
  isFetchingMore?: boolean;
}

function getDisplayName(member: Member): string {
  if (member.firstName && member.lastName) {
    return `${member.firstName} ${member.lastName}`;
  }
  if (member.firstName) {
    return member.firstName;
  }
  return member.email.split('@')[0];
}

function formatDate(dateString: string): string {
  return new Date(dateString).toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
  });
}

export function MembersTable({
  members,
  isLoading = false,
  isAdmin,
  currentUserEmail,
  onRoleChange,
  onRemove,
  invitationClerkIdByEmail,
  onResendInvite,
  onRevokeInvite,
  resendingInvitationId,
  hasMore = false,
  onLoadMore,
  isFetchingMore = false,
}: MembersTableProps) {
  const columns: ColumnDef<Member, string>[] = [
    {
      accessorKey: 'name',
      header: 'Name',
      size: 200,
      cell: ({ row }) => {
        const member = row.original;
        return (
          <div className="truncate font-medium">{getDisplayName(member)}</div>
        );
      },
    },
    {
      accessorKey: 'email',
      header: 'Email',
      cell: ({ row }) => (
        <span className="text-muted-foreground block min-w-[180px] truncate">
          {row.original.email}
        </span>
      ),
    },
    {
      accessorKey: 'role',
      header: 'Role',
      size: 140,
      cell: ({ row }) => {
        const member = row.original;
        const isCurrentUser = member.email === currentUserEmail;

        if (!isAdmin || isCurrentUser) {
          return (
            <span className="text-muted-foreground capitalize">
              {member.role}
            </span>
          );
        }

        return (
          <Select
            value={member.role}
            onValueChange={(value: 'admin' | 'member') =>
              onRoleChange?.(member.id, value)
            }
          >
            <SelectTrigger
              size="sm"
              className="w-[115px]"
              data-testid="role-select"
            >
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="admin">Admin</SelectItem>
              <SelectItem value="member">Member</SelectItem>
            </SelectContent>
          </Select>
        );
      },
    },
    {
      accessorKey: 'createdAt',
      header: 'Joined',
      size: 115,
      cell: ({ row }) => (
        <span className="text-muted-foreground text-sm">
          {formatDate(row.original.createdAt)}
        </span>
      ),
    },
    {
      accessorKey: 'status',
      header: 'Status',
      size: 80,
      cell: ({ row }) => {
        const status = row.original.status;
        const variant = status === 'active' ? 'success' : 'warning';
        return (
          <Badge variant={variant} className="capitalize">
            {status}
          </Badge>
        );
      },
    },
    {
      id: 'actions',
      header: '',
      size: 120,
      cell: ({ row }) => {
        const member = row.original;
        const isCurrentUser = member.email === currentUserEmail;

        // Pending invitations get resend + revoke (admin only), keyed by the Clerk
        // invitation id matched on the invitee's email. Hidden until that id loads.
        if (member.status === 'pending') {
          if (!isAdmin) {
            return null;
          }
          const clerkInvitationId = invitationClerkIdByEmail?.[member.email];
          // The pending member row is created the instant the invite is sent, but its
          // Clerk invitation id arrives a beat later via webhook. Until it lands, show
          // a disabled placeholder instead of an empty cell so the row never looks
          // broken; the actions appear once the invitation list catches up.
          if (!clerkInvitationId) {
            return (
              <div className="text-muted-foreground flex items-center justify-end gap-1.5 text-sm">
                <Loader2 className="h-3.5 w-3.5 animate-spin" />
                Pending…
              </div>
            );
          }
          const isResending = resendingInvitationId === clerkInvitationId;
          // Icon-only (tooltip + sr-only label) so both actions fit the column without
          // overflowing into the Status cell.
          return (
            <div className="flex justify-end gap-1">
              <Button
                variant="ghost"
                size="icon"
                onClick={() => onResendInvite?.(clerkInvitationId)}
                disabled={isResending}
                title={`Resend invitation to ${member.email}`}
              >
                {isResending ? (
                  <Loader2 className="h-4 w-4 animate-spin" />
                ) : (
                  <RotateCw className="h-4 w-4" />
                )}
                <span className="sr-only">
                  Resend invitation to {member.email}
                </span>
              </Button>
              <Button
                variant="ghost"
                size="icon"
                onClick={() => onRevokeInvite?.(member, clerkInvitationId)}
                className="text-destructive hover:text-destructive hover:bg-destructive/10"
                title={`Revoke invitation to ${member.email}`}
              >
                <Trash2 className="h-4 w-4" />
                <span className="sr-only">
                  Revoke invitation to {member.email}
                </span>
              </Button>
            </div>
          );
        }

        if (!isAdmin || isCurrentUser) {
          return null;
        }

        // Right-align (consistent with the pending-row actions). This also keeps the
        // trash's hover background at the far-right edge, away from the Status badge
        // in the previous cell.
        return (
          <div className="flex justify-end">
            <Button
              variant="ghost"
              size="sm"
              onClick={() => onRemove?.(member)}
              className="text-destructive hover:text-destructive hover:bg-destructive/10"
            >
              <Trash2 className="h-4 w-4" />
              Remove
              <span className="sr-only"> {member.email}</span>
            </Button>
          </div>
        );
      },
    },
  ];

  return (
    <>
      <DataTable<Member, string>
        columns={columns}
        data={members}
        isLoading={isLoading}
        emptyMessage="No team members yet."
      />
      {hasMore && (
        <div className="mt-4 flex justify-center">
          <Button
            variant="outline"
            size="sm"
            onClick={onLoadMore}
            disabled={isFetchingMore}
          >
            {isFetchingMore ? 'Loading…' : 'Load more'}
          </Button>
        </div>
      )}
    </>
  );
}
